import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { isPresent } from "@suse-o11y/is-present";
import type { Record } from "@suse-o11y/record";
import { z } from "zod";

import { matchPath } from "../../../generic/routing";

import { type SmartHistoryLocation, urlToSmartHistoryLocation } from "./SmartHistoryLocation";
import type { SmartHistoryRoute, SmartHistoryRoutePayload } from "./SmartHistoryRoute";
import type { SmartHistorySchemaPattern } from "./SmartHistorySchema";

/**
 * The entry is an item of the SmartHistoryQueue.
 */
export class SmartHistoryQueueEntry {
	/**
	 * The schema to decode an entry snapshot, so it can be matched against a schema's routes.
	 * An entry converts to a snapshot via `SmartHistoryQueueEntry#encode()`.
	 */
	public static schema = z.union([
		// if there are no keys, the entry is just a url
		z.string().transform((url) => ({
			location: urlToSmartHistoryLocation(url),
			keys: [],
		})),

		// otherwise, it is an array with the url followed by keys
		z
			.array(z.string())
			.min(1)
			.transform(([url, ...keys]) => ({
				location: urlToSmartHistoryLocation(url),
				keys,
			})),
	]);

	/**
	 * A fabric method to create an entry by matching the location to the route.
	 *
	 * @returns null if the location does not match the route or payload is null, otherwise an entry with the payload.
	 */
	public static match(
		{ pathname, search, key }: SmartHistoryLocation,
		{ path, extract }: SmartHistoryRoute,
		searchParams: Record<string>,
	): null | SmartHistoryQueueEntry {
		const result = matchPath(pathname, { path, exact: true });

		if (result == null) {
			return null;
		}

		const payload = extract ? extract(result.params, searchParams) : {};

		if (payload == null) {
			return null;
		}

		return new SmartHistoryQueueEntry(`${pathname}${search}`, path, payload, [key].filter(isPresent));
	}

	public readonly url: string;
	private readonly path: string;
	private readonly payload: SmartHistoryRoutePayload;
	private readonly keys: ReadonlySet<string>;

	/**
	 * The only way to create an entry is to match the location to the route via `SmartHistoryQueueEntry.match()`.
	 */
	private constructor(url: string, path: string, payload: SmartHistoryRoutePayload, keys: ReadonlyArray<string>) {
		this.url = url;
		this.path = path;
		this.payload = payload;
		this.keys = new Set(keys);
	}

	public addKeys(keys: ReadonlyArray<undefined | string>): SmartHistoryQueueEntry {
		const definedKeys = keys.filter(isPresent);

		return definedKeys.length === 0
			? this
			: new SmartHistoryQueueEntry(this.url, this.path, this.payload, [...this.keys, ...definedKeys]);
	}

	public hasKey(key: undefined | string): boolean {
		return key != null && this.keys.has(key);
	}

	public getKeys(): ReadonlyArray<string> {
		return [...this.keys];
	}

	/**
	 * Compares the entry to another entry by matching their paths and payloads.
	 */
	public isEqual(other: SmartHistoryQueueEntry): boolean {
		return this.path === other.path && isDeepEqual(this.payload, other.payload);
	}

	/**
	 * Encodes the minimum amount of information to restore the entry.
	 */
	public encode(): unknown {
		if (this.keys.size === 0) {
			return this.url;
		}

		return [this.url, ...this.keys];
	}

	/**
	 * Resolves the entry against the pattern.
	 * If the entry's path is not in the pattern, the default `pattern._()` is used.
	 */
	public resolve<TResult>(
		pattern: SmartHistorySchemaPattern<Record<string, SmartHistoryRoutePayload>, TResult>,
	): TResult {
		if (this.path in pattern) {
			return pattern[this.path](this.payload);
		}

		return pattern._();
	}
}
